import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { usePlayersStore, useBetsStore } from '../lib/store';
import styles from './PlayerDetail.module.css';

export default function PlayerDetail() {
	const { id } = useParams<{ id: string }>();
	const { selectedPlayer, isLoading, fetchPlayer } = usePlayersStore();
	const { bets, fetchBets } = useBetsStore();

	useEffect(() => {
		if (id) {
			fetchPlayer(id);
			fetchBets({ playerId: id });
		}
	}, [id, fetchPlayer, fetchBets]);

	if (isLoading && !selectedPlayer) {
		return (
			<div className={styles.loading}>
				<div className={styles.spinner} />
				<span>Loading player...</span>
			</div>
		);
	}

	if (!selectedPlayer) {
		return (
			<div className={styles.notFound}>
				<h2>Player not found</h2>
				<Link to="/players" className={styles.backLink}>
					Back to Players
				</Link>
			</div>
		);
	}

	return (
		<div className={styles.playerDetail}>
			<header className={styles.header}>
				<Link to="/players" className={styles.backLink}>
					<ArrowLeftIcon />
					<span>Back to Players</span>
				</Link>
			</header>

			{/* Player Header */}
			<div className={styles.playerHeader}>
				<div className={styles.playerInfo}>
					<div className={styles.avatar}>{selectedPlayer.externalId.charAt(0).toUpperCase()}</div>
					<div>
						<h1 className={styles.playerId}>{selectedPlayer.externalId}</h1>
						<p className={styles.playerMeta}>
							{selectedPlayer.currency} • Joined {formatDate(selectedPlayer.createdAt)}
						</p>
					</div>
				</div>
			</div>

			{/* Stats */}
			<div className={styles.statsGrid}>
				<div className={styles.statCard}>
					<span className={styles.statValue}>{selectedPlayer.totalBets.toLocaleString()}</span>
					<span className={styles.statLabel}>Total Bets</span>
				</div>
				<div className={styles.statCard}>
					<span className={styles.statValue}>{selectedPlayer.totalWagered}</span>
					<span className={styles.statLabel}>Total Wagered</span>
				</div>
				<div className={styles.statCard}>
					<span className={styles.statValue}>{selectedPlayer.totalWon}</span>
					<span className={styles.statLabel}>Total Won</span>
				</div>
				<div className={styles.statCard}>
					<span className={`${styles.statValue} ${selectedPlayer.netResult.startsWith('-') ? styles.negative : styles.positive}`}>{selectedPlayer.netResult}</span>
					<span className={styles.statLabel}>Net Result</span>
				</div>
			</div>

			{/* Bet History */}
			<section className={styles.section}>
				<h2 className={styles.sectionTitle}>Bet History</h2>
				<div className={styles.tableWrapper}>
					<table className={styles.table}>
						<thead>
							<tr>
								<th>Bet ID</th>
								<th>Game</th>
								<th>Amount</th>
								<th>Multiplier</th>
								<th>Payout</th>
								<th>Result</th>
								<th>Time</th>
							</tr>
						</thead>
						<tbody>
							{bets.length ? (
								bets.map((bet) => (
									<tr key={bet.id}>
										<td className={styles.betId}>{bet.id}</td>
										<td>{bet.game}</td>
										<td>{bet.amount}</td>
										<td>{bet.multiplier.toFixed(2)}x</td>
										<td>{bet.payout}</td>
										<td>
											<span className={`${styles.badge} ${bet.won ? styles.badgeSuccess : styles.badgeDanger}`}>{bet.won ? 'Win' : 'Loss'}</span>
										</td>
										<td className={styles.timestamp}>{formatTime(bet.createdAt)}</td>
									</tr>
								))
							) : (
								<tr>
									<td colSpan={7} className={styles.emptyState}>
										No bets found for this player
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</section>
		</div>
	);
}

function formatDate(timestamp: number): string {
	return new Date(timestamp).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
}

function formatTime(timestamp: number): string {
	const date = new Date(timestamp);
	return date.toLocaleString();
}

function ArrowLeftIcon() {
	return (
		<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<line x1="19" y1="12" x2="5" y2="12" />
			<polyline points="12 19 5 12 12 5" />
		</svg>
	);
}
